/**
 * Persistence helpers for the per-project `Authorization-Key` that
 * the TempControl API issues to anonymous draft owners.
 *
 * Storage layout (localStorage):
 *   - `tempcontrolAuthorizationKeys` -> JSON map of `{ [projectKey]: authorizationKey }`
 *   - `tempcontrolActiveProjectKey`  -> string project key of the most-recent draft,
 *                                       used to resume on next visit if no `?project=`
 *                                       query param is present.
 *
 * Mirrors the (commented) shower-configurator pattern. We intentionally use
 * localStorage rather than cookies so the auth key never travels with
 * unrelated requests and isn't subject to cookie size limits.
 */

const AUTH_KEYS_STORAGE_KEY = 'tempcontrolAuthorizationKeys';
const ACTIVE_PROJECT_STORAGE_KEY = 'tempcontrolActiveProjectKey';

type AuthKeyMap = Record<string, string>;

const safeReadJson = <T>(rawKey: string, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(rawKey);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return parsed as T;
  } catch {
    return fallback;
  }
};

const safeWriteJson = (rawKey: string, value: unknown) => {
  try {
    window.localStorage.setItem(rawKey, JSON.stringify(value));
  } catch {
    // ignore quota / privacy mode failures - the wizard still works,
    // it just won't persist the draft across reloads.
  }
};

export const readAuthKey = (projectKey: string): string | undefined => {
  const map = safeReadJson<AuthKeyMap>(AUTH_KEYS_STORAGE_KEY, {});
  return map[projectKey];
};

/**
 * Snapshot of every `{ projectKey -> authorizationKey }` pair held
 * in localStorage. Drives the wizard's history panel so we can
 * re-hydrate each anonymous draft with one round-trip per key.
 */
export const readAuthKeyMap = (): Readonly<AuthKeyMap> => {
  return safeReadJson<AuthKeyMap>(AUTH_KEYS_STORAGE_KEY, {});
};

export const writeAuthKey = (projectKey: string, authorizationKey: string): void => {
  const map = safeReadJson<AuthKeyMap>(AUTH_KEYS_STORAGE_KEY, {});
  map[projectKey] = authorizationKey;
  safeWriteJson(AUTH_KEYS_STORAGE_KEY, map);
};

export const removeAuthKey = (projectKey: string): void => {
  const map = safeReadJson<AuthKeyMap>(AUTH_KEYS_STORAGE_KEY, {});
  delete map[projectKey];
  safeWriteJson(AUTH_KEYS_STORAGE_KEY, map);
};

export const readActiveProjectKey = (): string | undefined => {
  try {
    return window.localStorage.getItem(ACTIVE_PROJECT_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

export const writeActiveProjectKey = (projectKey: string): void => {
  try {
    window.localStorage.setItem(ACTIVE_PROJECT_STORAGE_KEY, projectKey);
  } catch {
    // ignore
  }
};

export const clearActiveProjectKey = (): void => {
  try {
    window.localStorage.removeItem(ACTIVE_PROJECT_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const clearProjectStorage = (projectKey: string): void => {
  removeAuthKey(projectKey);
  if (readActiveProjectKey() === projectKey) {
    clearActiveProjectKey();
  }
};
